// Owner: WP03 (PLAY01-06). Public player API consumed by every page. Keep this surface stable;
// changes need integration lead review. One persistent playback session survives route changes (UX01).
import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useReducer, useRef, useState,
  useSyncExternalStore, type ReactNode,
} from 'react';
import {
  LayoutList, Maximize, Maximize2, Minimize, Minimize2, Pause, PictureInPicture2, Play, Radio, Settings, Undo2, Volume2, VolumeX, X,
} from 'lucide-react';
import { QueryClientContext } from '@tanstack/react-query';
import type { PlaybackSession, Progress, SeriesDetail, StateOperation } from '@tuned/contracts';
import { api } from '../../app/api.ts';
import { useUserState } from '../../store/index.ts';
import { formatLeft, formatTimeRange } from '../../components/format.ts';
import { episodeRequest, nextEpisodeAfter } from '../series/seasons.ts';
import { createController, type Controller, type ControllerOptions, type ControllerSnapshot } from './controller.ts';
import type { EngineName } from './engine.ts';
import type { PlayRequest, PlayerError, PlayerStatus } from './machine.ts';
import { ErrorPanel } from './ErrorPanel.tsx';
import { ExternalPanel } from './ExternalPanel.tsx';
import { GuideOverlay } from './GuideOverlay.tsx';
import './player.css';

export type { PlayRequest, PlayerStatus, PlayerError } from './machine.ts';
export type PlayerApi = {
  status: PlayerStatus;
  current: PlayRequest | null;
  error: PlayerError | null;
  play(req: PlayRequest): void;
  stop(): void;
  /** Expand to full-screen player view / collapse to mini player. */
  setExpanded(expanded: boolean): void;
  expanded: boolean;
  // Additive (WP03):
  session: PlaybackSession | null;
  engine: EngineName | null;
  /** Last 10 live channels, most recent first. */
  recent: PlayRequest[];
  /** Plays recent[1]. */
  previousChannel(): void;
  retry(): void;
  /** stop(), resolving once the lease DELETE has settled (≤3 s). Await before opening another stream on tile 0. */
  release(): Promise<void>;
};

const noop: PlayerApi = {
  status: 'idle', current: null, error: null, play() {}, stop() {}, setExpanded() {}, expanded: false,
  session: null, engine: null, recent: [], previousChannel() {}, retry() {}, release: async () => {},
};
const Ctx = createContext<PlayerApi>(noop);
const ShellCtx = createContext<() => () => void>(() => () => {});
export const usePlayer = () => useContext(Ctx);

const IDLE: ControllerSnapshot = {
  status: 'idle', error: null, request: null, session: null, engine: null, reconnects: 0, maxReconnects: 2, parked: false, tracks: 0,
  diagnostics: { engine: null, delivery: null, mediaPath: null, lastHttpStatus: null, transport: null, decision: null },
};
const idleSubscribe = () => () => {};
const idleSnapshot = () => IDLE;
const BUSY: ReadonlySet<PlayerStatus> = new Set(['resolving', 'connecting', 'buffering', 'recovering']);
/** Statuses that hold a lease (an external link keeps its lease while its panel is open). */
export const ACTIVE_STATUSES: ReadonlySet<PlayerStatus> = new Set(['connecting', 'playing', 'paused', 'buffering', 'recovering', 'ended', 'external']);

/**
 * One playback slot: a <video> created once and appended to `stageRef` (never detached while mounted) plus its
 * controller. Used by the main player (tile 0) and every multiview tile.
 */
export function usePlaybackTile(tile: number, opts: {
  onProgress?: (op: StateOperation) => void; videoTestId?: string; muted?: boolean;
  preferences?: ControllerOptions['preferences']; onPreferences?: ControllerOptions['onPreferences'];
} = {}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [ctrl, setCtrl] = useState<Controller | null>(null);
  const latest = useRef(opts);
  useLayoutEffect(() => { latest.current = opts; });
  const { videoTestId, muted } = opts;
  useEffect(() => {
    const video = document.createElement('video');
    video.playsInline = true;
    video.muted = !!muted;
    if (videoTestId) video.dataset.testid = videoTestId;
    stageRef.current?.appendChild(video);
    const c = createController({
      video, tile,
      onProgress: (op) => latest.current.onProgress?.(op),
      preferences: () => latest.current.preferences?.() ?? null,
      onPreferences: (p) => latest.current.onPreferences?.(p),
    });
    setCtrl(c);
    return () => { c.dispose(); video.remove(); };
  }, [tile]); // eslint-disable-line react-hooks/exhaustive-deps -- created once per tile; later option changes apply via effects
  const snap = useSyncExternalStore(ctrl?.subscribe ?? idleSubscribe, ctrl?.getSnapshot ?? idleSnapshot);
  return { ctrl, snap, stageRef };
}

/** Re-render on media clock/volume/fullscreen/PiP changes. */
export function useMediaTick(v: HTMLVideoElement | undefined) {
  const [, tick] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    if (!v) return;
    const evs = ['timeupdate', 'durationchange', 'volumechange', 'progress', 'seeked', 'emptied', 'enterpictureinpicture', 'leavepictureinpicture'];
    for (const e of evs) v.addEventListener(e, tick);
    document.addEventListener('fullscreenchange', tick);
    return () => {
      for (const e of evs) v.removeEventListener(e, tick);
      document.removeEventListener('fullscreenchange', tick);
    };
  }, [v]);
}

const channelKey = (r: PlayRequest) => r.channelId ?? r.sourceId;
export function clock(sec: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(sec) ? sec : 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

type TestWindow = Window & { __tunedPlayer?: unknown };

/** Wraps the app; owns the single persistent <video> element and playback session. */
export function PlayerProvider({ children }: { children: ReactNode }) {
  const { state, mutate, progressFor } = useUserState();
  const prefs = state?.preferences ?? null;
  const { ctrl, snap, stageRef } = usePlaybackTile(0, {
    onProgress: mutate, videoTestId: 'player-video',
    preferences: () => prefs,
    onPreferences: (p) => mutate({ op: 'preferences.set', preferences: p }),
  });
  const [expanded, setExpanded] = useState(false);
  const [recent, setRecent] = useState<PlayRequest[]>([]);
  const [shells, setShells] = useState(0);
  const markShell = useCallback(() => { setShells((n) => n + 1); return () => setShells((n) => n - 1); }, []);

  // play/stop/release/previousChannel/retry are referentially stable and read refs, so callers holding an older
  // copy of the player object (toasts, detail panes) still drive the current controller. A play() that lands before
  // the controller exists is queued, not dropped.
  const ctrlRef = useRef<Controller | null>(null);
  const pendingRef = useRef<PlayRequest | null>(null);
  const recentRef = useRef(recent);
  useLayoutEffect(() => { recentRef.current = recent; });
  useLayoutEffect(() => {
    ctrlRef.current = ctrl;
    const req = pendingRef.current;
    pendingRef.current = null;
    if (ctrl && req) void ctrl.play(req);
  }, [ctrl]);
  const play = useCallback((req: PlayRequest) => {
    if (req.mode === 'live') setRecent((r) => [req, ...r.filter((x) => channelKey(x) !== channelKey(req))].slice(0, 10));
    const c = ctrlRef.current;
    if (c) void c.play(req); else pendingRef.current = req;
  }, []);
  const release = useCallback(() => { setExpanded(false); pendingRef.current = null; return ctrlRef.current?.stop() ?? Promise.resolve(); }, []);
  const stop = useCallback(() => { void release(); }, [release]);
  const previousChannel = useCallback(() => { const r = recentRef.current[1]; if (r) play(r); }, [play]);
  const retry = useCallback(() => { void ctrlRef.current?.retry(); }, []);

  const value = useMemo<PlayerApi>(() => ({
    status: snap.status, current: snap.request, error: snap.error, play, stop, setExpanded, expanded,
    session: snap.session, engine: snap.engine, recent, previousChannel, retry, release,
  }), [snap, play, stop, expanded, recent, previousChannel, retry, release]); // callbacks above are stable

  // XC05: the server records catch-up/start-over as verified on the first successful replay, so the guide's archive
  // states may now be 'available': refetch them once per replay session. (Optional context: tests may omit it.)
  const qc = useContext(QueryClientContext);
  const replayChecked = useRef<string | null>(null);
  useEffect(() => {
    const id = snap.session?.sessionId;
    const replay = snap.request?.mode === 'catchup' || snap.request?.mode === 'start_over';
    if (snap.status !== 'playing' || !replay || !id || replayChecked.current === id) return;
    replayChecked.current = id;
    void qc?.invalidateQueries({ queryKey: ['guide'] });
    void qc?.invalidateQueries({ queryKey: ['home'] });
  }, [qc, snap.status, snap.session, snap.request]);

  useEffect(() => {
    if (!ctrl) return;
    const art = snap.request?.artworkId;
    if (art) ctrl.video.poster = `/api/artwork/${encodeURIComponent(art)}`;
    else ctrl.video.removeAttribute('poster');
  }, [ctrl, snap.request?.artworkId]);

  // E2E seam (dev only): Playwright drives and inspects the real player through this.
  useEffect(() => {
    if (!(import.meta.env.DEV || import.meta.env.VITE_E2E === '1') || !ctrl) return;
    const w = window as TestWindow;
    w.__tunedPlayer = {
      play: (r: PlayRequest) => play(r),
      stop: () => ctrl.stop(),
      get status() { return ctrl.getSnapshot().status; },
      get error() { return ctrl.getSnapshot().error; },
      get session() { const s = ctrl.getSnapshot().session; return s && { ...s }; },
      get request() { const r = ctrl.getSnapshot().request; return r && { ...r }; },
      get tracks() {
        const h = ctrl.engine();
        return h && { audio: h.audioTracks(), audioTrack: h.audioTrack(), subtitles: h.subtitleTracks(), subtitleTrack: h.subtitleTrack() };
      },
      get engine() { return ctrl.getSnapshot().engine; },
      get transport() { return ctrl.getSnapshot().diagnostics.transport; },
      get decision() { return ctrl.getSnapshot().diagnostics.decision; },
      get lastFault() { return ctrl.getSnapshot().diagnostics.lastFault ?? null; },
      video: ctrl.video,
      get firstFrameMs() { return ctrl.firstFrameMs(); },
    };
    return () => { delete w.__tunedPlayer; };
  }, [ctrl, play]);

  const title = snap.request?.title ?? '';
  const announce: Record<PlayerStatus, string> = {
    idle: '', resolving: `Connecting to ${title}`, connecting: `Connecting to ${title}`, playing: `Playing ${title}`, paused: 'Paused',
    buffering: 'Buffering', recovering: `Reconnecting, attempt ${snap.reconnects} of ${snap.maxReconnects}`, ended: 'Playback ended', failed: '',
    external: `${title} opens in another player`,
  };

  return (
    <Ctx.Provider value={value}>
      <ShellCtx.Provider value={markShell}>{children}</ShellCtx.Provider>
      <section
        className={`tp ${expanded ? 'tp-expanded' : 'tp-mini'}${shells ? '' : ' tp-noshell'}`}
        data-testid="player-dock"
        data-player-status={snap.status}
        hidden={snap.status === 'idle'}
        aria-label="Player"
        onKeyDown={(e) => { if (e.key === 'Escape' && expanded && !document.fullscreenElement) setExpanded(false); }}
      >
        <div className="tp-stage" ref={stageRef} />
        {BUSY.has(snap.status) && <div className="tp-spinner" aria-hidden="true" />}
        {ctrl && snap.status !== 'idle' && (
          <PlayerChrome ctrl={ctrl} snap={snap} expanded={expanded} setExpanded={setExpanded} recent={recent} play={play} previousChannel={previousChannel} close={stop}
            autoplayNext={prefs?.autoplayNextEpisode ?? false} progressFor={progressFor} />
        )}
      </section>
      <div className="visually-hidden" role="status" aria-live="polite">{announce[snap.status]}</div>
    </Ctx.Provider>
  );
}

type WebkitVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void };

function PlayerChrome({ ctrl, snap, expanded, setExpanded, recent, play, previousChannel, close, autoplayNext, progressFor }: {
  ctrl: Controller; snap: ControllerSnapshot; expanded: boolean; setExpanded(v: boolean): void;
  recent: PlayRequest[]; play(r: PlayRequest): void; previousChannel(): void; close(): void;
  autoplayNext: boolean; progressFor(itemId: string): Progress | null;
}) {
  const v = ctrl.video as WebkitVideo;
  useMediaTick(v);
  const req = snap.request;
  const s = snap.session;
  const live = s?.capabilities.live ?? req?.mode === 'live';
  const li = ctrl.liveInfo();
  const behind = !!li && !li.expired && li.delay > 10;
  const t = s?.timeline;
  const range = t?.programStart && t.programEnd ? formatTimeRange(t.programStart, t.programEnd) : null;
  const statusText: Record<PlayerStatus, string> = {
    idle: '', resolving: 'Connecting…', connecting: 'Connecting…', playing: live ? 'Playing live' : 'Playing', paused: 'Paused',
    buffering: 'Buffering…', recovering: `Reconnecting (${snap.reconnects} of ${snap.maxReconnects})…`, ended: 'Ended', failed: "Couldn't play",
    external: 'Opens in another player',
  };
  const muted = v.muted || v.volume === 0;
  const fsSupported = !!document.fullscreenEnabled || typeof v.webkitEnterFullscreen === 'function';
  const pip = !!document.pictureInPictureEnabled && s?.capabilities.pictureInPicture !== false && !!s;
  const root = () => v.closest('.tp') as HTMLElement | null;
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else if (document.fullscreenEnabled) void root()?.requestFullscreen();
    else v.webkitEnterFullscreen?.();
  };
  const togglePip = () => {
    if (document.pictureInPictureElement) void document.exitPictureInPicture();
    else void v.requestPictureInPicture().catch(() => {});
  };
  const prev = live && recent.length > 1 && channelKey(recent[0]!) === (req && channelKey(req)) ? recent[1] : undefined;
  const [guideOpen, setGuideOpen] = useState(false);
  const guideBtn = useRef<HTMLButtonElement>(null);
  const showGuide = guideOpen && expanded && live;
  useEffect(() => { if (!expanded) setGuideOpen(false); }, [expanded]); // collapsing to the mini player closes it
  const closeGuide = () => { setGuideOpen(false); guideBtn.current?.focus(); };

  return (
    <>
      {showGuide && <GuideOverlay currentChannelId={req?.channelId ?? null} recent={recent} onPlay={play} onClose={closeGuide} />}
      <div className="tp-bar">
        <div className="tp-meta">
          <p className="tp-title">{req?.title}</p>
          <p className="tp-status">
            <span className={`tp-dot${snap.status === 'playing' ? ' tp-dot-on' : ''}`} aria-hidden="true" />
            <span>{statusText[snap.status]}</span>
            {range && <span className="tp-muted"> · {range}</span>}
          </p>
          {snap.diagnostics.transport === 'downgraded' && (
            <p className="tp-note" title="Your provider redirected this stream from HTTPS to plain HTTP.">Unencrypted source</p>
          )}
          {expanded && req?.subtitle && <p className="tp-muted">{req.subtitle}</p>}
        </div>
        <div className="tp-timeline"><Timeline ctrl={ctrl} session={s} /></div>
        <div className="tp-controls">
          {prev && (
            <button type="button" className="tp-icon tp-hide-sm" aria-label={`Previous channel: ${prev.title}`} onClick={previousChannel}><Undo2 aria-hidden="true" /></button>
          )}
          {li?.expired ? (
            <button type="button" className="tp-btn tp-btn-primary" onClick={() => ctrl.togglePlay()}>Jump to live</button>
          ) : (
            <button type="button" className="tp-icon" aria-label={v.paused ? 'Play' : 'Pause'} onClick={() => ctrl.togglePlay()} disabled={snap.status === 'failed' || snap.status === 'external'}>
              {v.paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
            </button>
          )}
          {behind && (
            <button type="button" className="tp-btn" onClick={() => ctrl.goLive()}><Radio aria-hidden="true" /> Go live</button>
          )}
          <span className="tp-volume tp-hide-sm">
            <button type="button" className="tp-icon" aria-label={muted ? 'Unmute' : 'Mute'} onClick={() => { v.muted = !muted; if (!v.muted && v.volume === 0) v.volume = 0.5; }}>
              {muted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
            </button>
            <input type="range" className="tp-range" aria-label="Volume" min={0} max={1} step={0.05} value={muted ? 0 : v.volume}
              onChange={(e) => { const x = Number(e.target.value); v.volume = x; v.muted = x === 0; }} />
          </span>
          <TrackMenu ctrl={ctrl} version={snap.tracks} />
          {live && (
            <button ref={guideBtn} type="button" className="tp-icon" aria-label="Channel guide" aria-expanded={showGuide}
              onClick={() => { if (showGuide) closeGuide(); else { setExpanded(true); setGuideOpen(true); } }}>
              <LayoutList aria-hidden="true" />
            </button>
          )}
          {pip && (
            <button type="button" className="tp-icon tp-hide-sm" aria-label={document.pictureInPictureElement ? 'Exit picture in picture' : 'Picture in picture'} onClick={togglePip}>
              <PictureInPicture2 aria-hidden="true" />
            </button>
          )}
          <button type="button" className="tp-icon" aria-label={expanded ? 'Collapse player' : 'Expand player'} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
            {expanded ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
          </button>
          {fsSupported && (
            <button type="button" className="tp-icon tp-hide-sm" aria-label={document.fullscreenElement ? 'Exit full screen' : 'Full screen'} onClick={toggleFullscreen}>
              {document.fullscreenElement ? <Minimize aria-hidden="true" /> : <Maximize aria-hidden="true" />}
            </button>
          )}
          <button type="button" className="tp-icon" aria-label="Close player" onClick={close}><X aria-hidden="true" /></button>
        </div>
      </div>
      {expanded && recent.length > 1 && (
        <nav className="tp-recent" aria-label="Recent channels">
          {recent.filter((r) => !req || channelKey(r) !== channelKey(req)).map((r) => (
            <button type="button" key={channelKey(r)} className="tp-btn" onClick={() => play(r)}>{r.title}</button>
          ))}
        </nav>
      )}
      {snap.status === 'ended' && req?.mode === 'vod' && req.itemId && req.seriesId && (
        <NextEpisode key={s?.sessionId ?? req.itemId} itemId={req.itemId} seriesId={req.seriesId} autoplay={autoplayNext} play={play} progressFor={progressFor} />
      )}
      {snap.status === 'external' && s && <ExternalPanel url={s.playbackUrl} onClose={close} />}
      {snap.status === 'failed' && snap.error && (
        <ErrorPanel error={snap.error} diagnostics={snap.diagnostics} onRetry={() => void ctrl.retry()} onClose={close}
          onStopSession={(id) => void ctrl.stopSessionAndRetry(id)} />
      )}
    </>
  );
}

function Timeline({ ctrl, session }: { ctrl: Controller; session: PlaybackSession | null }) {
  const v = ctrl.video;
  if (!session) return null;
  if (!session.capabilities.live) {
    const d = v.duration;
    if (!Number.isFinite(d) || d <= 0) return null;
    if (!session.capabilities.seekable) {
      return <><Bar value={v.currentTime / d} label="Playback position" /><span className="tp-time">Seeking isn't available for this title</span></>;
    }
    return (
      <>
        <input type="range" className="tp-range tp-seek" aria-label="Seek" min={0} max={d} step={1} value={Math.min(v.currentTime, d)}
          aria-valuetext={`${clock(v.currentTime)} of ${clock(d)}`} onChange={(e) => ctrl.seekTo(Number(e.target.value))} />
        <span className="tp-time">{clock(v.currentTime)} / {clock(d)}</span>
      </>
    );
  }
  const li = ctrl.liveInfo();
  const w = li?.window;
  if (li && w && w.end - w.start >= 30) {
    return (
      <>
        <input type="range" className="tp-range tp-seek" aria-label="Seek within the live buffer" min={w.start} max={w.end} step={1}
          value={Math.min(Math.max(v.currentTime, w.start), w.end)} aria-valuetext={li.delay > 10 ? `${clock(li.delay)} behind live` : 'At live'}
          onChange={(e) => ctrl.seekTo(Number(e.target.value))} />
        <span className="tp-time">{li.delay > 10 ? `Live delay ${clock(li.delay)}` : 'Live'}</span>
      </>
    );
  }
  const t = session.timeline;
  if (!t?.programStart || !t.programEnd) return null;
  const a = Date.parse(t.programStart), b = Date.parse(t.programEnd), now = Date.now();
  return (
    <>
      <Bar value={(now - a) / (b - a)} label="Programme progress" />
      <span className="tp-time">{now < b ? formatLeft((b - now) / 1000) : 'Ended'}</span>
    </>
  );
}

function Bar({ value, label }: { value: number; label: string }) {
  const pct = Math.round(Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0)) * 100);
  return <div className="tp-bar-track" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}><span style={{ width: `${pct}%` }} /></div>;
}

export const NEXT_EPISODE_SEC = 10;

/** VOD02: when an episode ends, offer the next one; with autoplay opted in, count down and start it. */
function NextEpisode({ itemId, seriesId, autoplay, play, progressFor }: {
  itemId: string; seriesId: string; autoplay: boolean; play(r: PlayRequest): void; progressFor(itemId: string): Progress | null;
}) {
  const [next, setNext] = useState<PlayRequest | null>(null);
  const [left, setLeft] = useState(NEXT_EPISODE_SEC);
  const [cancelled, setCancelled] = useState(false);
  const progressRef = useRef(progressFor);
  useLayoutEffect(() => { progressRef.current = progressFor; });
  useEffect(() => {
    let live = true;
    api<{ series: SeriesDetail }>('GET', `/api/series/${encodeURIComponent(seriesId)}`)
      .then(({ series }) => {
        const e = nextEpisodeAfter(series.seasons, itemId);
        if (live && e) setNext(episodeRequest(series, e, progressRef.current(e.id) ?? e.progress));
      })
      .catch(() => {});   // series unavailable: no next episode is offered
    return () => { live = false; };
  }, [seriesId, itemId]);
  const counting = !!next && autoplay && !cancelled;
  useEffect(() => {
    if (!counting || !next) return;
    if (left <= 0) { play(next); return; }
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [counting, left, next, play]);
  const label = next?.subtitle ?? next?.title ?? '';
  return (
    <>
      <p className="visually-hidden" role="status">{counting ? `Next episode, ${label}, starts in ${NEXT_EPISODE_SEC} seconds` : ''}</p>
      {next && (
        <div className="tp-next" role="group" aria-label="Next episode"
          onKeyDown={(e) => { if (e.key === 'Escape' && counting) { e.stopPropagation(); setCancelled(true); } }}>
          {counting && <p className="tp-next-count">Next episode in {left} s</p>}
          <p className="tp-muted">{label}</p>
          {counting ? (
            <>
              <button type="button" className="tp-btn tp-btn-primary" onClick={() => play(next)}>Play now</button>
              <button type="button" className="tp-btn" onClick={() => setCancelled(true)}>Cancel</button>
            </>
          ) : (
            <button type="button" className="tp-btn tp-btn-primary" onClick={() => play(next)}><Play aria-hidden="true" /> Play next episode</button>
          )}
        </div>
      )}
    </>
  );
}

/** Captions, audio and quality — rendered only when the stream actually offers a choice. */
function TrackMenu({ ctrl, version }: { ctrl: Controller; version: number }) {
  const [open, setOpen] = useState(false);
  const [, force] = useReducer((x: number) => x + 1, 0);
  const id = useId();
  const btn = useRef<HTMLButtonElement>(null);
  const h = ctrl.engine();
  void version;
  const levels = h?.levels() ?? [], audio = h?.audioTracks() ?? [], subs = h?.subtitleTracks() ?? [];
  if (!h || (levels.length < 2 && audio.length < 2 && subs.length === 0)) return null;
  const pick = (set: (id: number) => void) => (e: { target: { value: string } }) => { set(Number(e.target.value)); force(); };
  return (
    <span className="tp-menu-wrap tp-hide-sm">
      <button ref={btn} type="button" className="tp-icon" aria-label="Captions, audio and quality" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
        <Settings aria-hidden="true" />
      </button>
      {open && (
        <div id={id} className="tp-menu" role="group" aria-label="Playback settings"
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); btn.current?.focus(); } }}>
          {subs.length > 0 && (
            <label>Captions
              <select value={h.subtitleTrack()} onChange={pick((id) => ctrl.chooseTrack('subtitle', id))}>
                <option value={-1}>Off</option>
                {subs.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
          )}
          {audio.length > 1 && (
            <label>Audio
              <select value={h.audioTrack()} onChange={pick((id) => ctrl.chooseTrack('audio', id))}>
                {audio.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
          )}
          {levels.length > 1 && (
            <label>Quality
              <select value={h.level()} onChange={pick(h.setLevel)}>
                <option value={-1}>Auto</option>
                {levels.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
    </span>
  );
}

/** Rendered once by the Shell: marks the shell as present and reserves room for the fixed dock. */
export function PlayerDock() {
  const { status, expanded } = usePlayer();
  const mark = useContext(ShellCtx);
  useEffect(mark, [mark]);
  return status === 'idle' || expanded ? null : <div className="tp-spacer" aria-hidden="true" />;
}
